import Feature from 'ol/Feature.js';
import Polygon from 'ol/geom/Polygon';
import type { Coordinate } from 'ol/coordinate';
import Draw, { createBox } from 'ol/interaction/Draw';
import VectorLayer from 'ol/layer/Vector';
import Map from 'ol/Map';
import { toLonLat } from 'ol/proj.js';
import VectorSource from 'ol/source/Vector';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import { toast } from 'react-toastify';
import { AoiData } from '../Map.types';
import { getArea } from '../maputils.ts';
import IS_EFAS from '../../../components/sitebase/Environment.ts';
import { getCSRFToken } from '../../../components/sitebase/components/CookieHelper';

export class GfmAoiService {
    private readonly map: Map;
    private gfmAoiLayer: VectorLayer<VectorSource>;
    private gfmDraw: Draw | null = null;

    constructor(map: Map) {
        this.map = map;
        this.gfmAoiLayer = new VectorLayer({
            source: new VectorSource({
                features: []
            }),
            style: new Style({
                fill: new Fill({ color: '#add8e63a' }),
                stroke: new Stroke({ color: 'blue', width: 2 })
            }),
        });
    }

    isDrawing(): boolean {
        return this.gfmDraw !== null;
    }

    show(aoi: AoiData): void {
        const src = this.gfmAoiLayer.getSource();
        try {
            this.map.addLayer(this.gfmAoiLayer);
        } catch {
            console.debug("GFM AOI layer already added to the map");
        }

        if (src) {
            let polygon = new Polygon(aoi.geoJSON.coordinates);
            polygon = polygon.transform('EPSG:4326', 'EPSG:3857');

            const feature = new Feature({
                geometry: polygon,
                name: aoi.aoi_name,
            });
            feature.setId(aoi.aoi_id);
            src.addFeature(feature);
            this.gfmAoiLayer.setVisible(true);

            const view = this.map.getView();
            view.fit(polygon, { padding: [100, 50, 100, 50], duration: 500 });
        }
    }

    hide(aoi: AoiData): void {
        const src = this.gfmAoiLayer.getSource();
        if (src) {
            const feature = src.getFeatureById(aoi.aoi_id);
            if (feature)
                src.removeFeature(feature);
        }
    }

    addInteraction(drawEndCallback: () => void): void {
        this.clearInteraction();
        this.gfmAoiLayer.setVisible(true);
        this.map.addLayer(this.gfmAoiLayer);
        this.gfmDraw = new Draw({
            source: this.gfmAoiLayer.getSource() as VectorSource,
            type: 'Circle',
            geometryFunction: createBox(),
        });

        this.gfmDraw.on('drawend', (evt) => {
            const feature = evt.feature;

            if (feature) {
                const geometry = feature.getGeometry();
                const coords = geometry?.getCoordinates();
                const lonlatCords = coords[0].map((coord: Coordinate) => toLonLat(coord));
                const area = getArea([lonlatCords]);
                if (area > 1000000 * 1000000) {
                    toast.error(`The drawn area is too large ${(area / 1000000).toFixed(0)}km, please draw an area smaller than 1 million square km`);
                    this.clearInteraction();
                }
            }
            if (this.gfmDraw) this.map.removeInteraction(this.gfmDraw);
            drawEndCallback();
        });

        this.map.addInteraction(this.gfmDraw);
    }

    clearInteraction(): void {
        this.gfmAoiLayer.setVisible(false);
        const src = this.gfmAoiLayer.getSource();
        if (src) {
            src.clear();
        }
        this.map.removeLayer(this.gfmAoiLayer);
        if (this.gfmDraw) {
            this.map.removeInteraction(this.gfmDraw);
            this.gfmDraw = null;
        }
    }

    createAoi(name: string, description: string, onResult: () => void): void {
        const src = this.gfmAoiLayer.getSource();
        if (src) {
            const features = src.getFeatures();
            if (features.length === 0) {
                toast.error("No AOI drawn, please draw an AOI first");
                return;
            }
            if (features.length > 1) {
                toast.error("Multiple AOIs drawn, please draw only one AOI at a time");
                return;
            }
            const feature = features[0];
            const geometry = feature.getGeometry();
            const coords = geometry?.getCoordinates();
            if (!coords) {
                toast.error("Error getting AOI coordinates, please try again");
                return;
            }
            // make a GeoJSON and fetch method post it
            const polygon = { type: "Polygon", coordinates: [coords[0].map((coord: Coordinate) => toLonLat(coord))] };
            console.log("Creating AOI with polygon", polygon);
            fetch(IS_EFAS ? "/api/gfm/aoi-create/" : "/api/v1/gfm-aoi-create/", {
                method: "POST",
                body: JSON.stringify({ aoi_name: name, aoi_description: description, geoJSON: polygon }),
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    ...(!IS_EFAS && { "X-CSRFToken": getCSRFToken() }),
                }
            }).then((response) => {
                if (response.status === 201) {
                    console.log("AOI created successfully");
                }
            }).catch((error) => {
                console.error("Error creating AOI", error);
            });

            fetch(IS_EFAS ? "/api/gfm/aoi-can-create/" : "/api/v1/gfm-aoi-can-create/", {
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                }
            }).then((response) => {
                if (response.status === 201) {
                    toast.success("AOI created successfully");
                    this.clearInteraction();
                    return response.json();
                } else {
                    toast.error("Error creating AOI, please try again");
                }
            }).catch((error) => {
                console.error("Error creating AOI", error);
                toast.error("Error creating AOI, please try again");
            }).finally(() => {
                onResult();
            });
        }
    }
}
